import type {
  Query,
  QueryClient,
  QueryKey,
  StaleTime,
} from "@tanstack/react-query";

function isStaleByEffectiveTime<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
>(query: Query<TQueryFnData, TError, TData, TQueryKey>): boolean {
  const staleTimeOption = (
    query.options as typeof query.options & {
      staleTime?: StaleTime<TQueryFnData, TError, TData, TQueryKey>;
    }
  ).staleTime;
  const staleTime =
    typeof staleTimeOption === "function"
      ? staleTimeOption(query)
      : staleTimeOption;
  return query.isStaleByTime(staleTime);
}

function hasExactAuthority(queryClient: QueryClient, queryKey: QueryKey) {
  const query = queryClient.getQueryCache().find({ queryKey, exact: true });
  const state = query?.state;
  return Boolean(
    query &&
      state?.data !== undefined &&
      state.status === "success" &&
      state.fetchStatus === "idle" &&
      state.error === null &&
      !state.isInvalidated &&
      !isStaleByEffectiveTime(query),
  );
}

export function exactQueryIsAuthoritative(
  queryClient: QueryClient,
  queryKey: QueryKey,
): boolean {
  return hasExactAuthority(queryClient, queryKey);
}

export function exactAuthoritativeData<T>(
  queryClient: QueryClient,
  queryKey: QueryKey,
): T | undefined {
  return hasExactAuthority(queryClient, queryKey)
    ? queryClient.getQueryData<T>(queryKey)
    : undefined;
}

export function governanceStateVersion(
  queryClient: QueryClient,
  facilityId: string,
): string {
  return ["facility", "facility-approval-readiness"]
    .map((kind) => {
      const query = queryClient
        .getQueryCache()
        .find({ queryKey: ["platform", kind, facilityId], exact: true });
      const state = query?.state;
      const stale = query ? isStaleByEffectiveTime(query) : true;
      return `${state?.dataUpdateCount}:${state?.errorUpdateCount}:${state?.status}:${state?.fetchStatus}:${state?.isInvalidated}:${stale}`;
    })
    .join("|");
}
